import { useEffect, useRef } from 'react';
import { endpoints } from '@/api';
import type { AdsConfig } from '@/api/types';
import { useAuth } from '@/stores/auth';
import { skipBetweenCallsAd } from './betweenCalls';
import { useAdOverlay } from './overlay';
import { resetAdProvider } from './runtime';
import { useAdsConfig } from './useAds';

/**
 * Ads are switched at runtime by an admin (ARCHITECTURE §22): `GET /v1/config`
 * is refetched every minute and on focus (useConfig), and this module reacts
 * to what changed.
 */

/** What identifies a provider's configuration ("" = ads off). */
export function adsSignature(ads: AdsConfig | null | undefined): string {
  if (!ads) return '';
  const u = ads.units;
  return [ads.network_code, ads.test_mode ? 'test' : 'live', u.rewarded, u.interstitial, u.home_card, u.drops_card].join('|');
}

/** Ads turned off: destroy every slot and close any ad chrome on screen. */
export function teardownAds(): void {
  resetAdProvider();
  const { overlay, set } = useAdOverlay.getState();
  if (!overlay) return;
  if (overlay.kind === 'fake-rewarded') overlay.resolve('closed');
  else if (overlay.kind === 'fake-interstitial') {
    set(null);
    overlay.resolve();
  } else skipBetweenCallsAd();
}

/**
 * Mounted once (AdsHost). Ads off ⇒ tear everything down. Units changed (a
 * placement switched) ⇒ rebuild the provider on next use; cards re-render
 * from the new config by themselves. Ads on ⇒ refresh `Me`, whose
 * `ads_eligible` was computed while they were off.
 */
export function useAdsLiveSync(): void {
  const sig = adsSignature(useAdsConfig());
  const prev = useRef(sig);
  useEffect(() => {
    const was = prev.current;
    if (was === sig) return;
    prev.current = sig;
    if (!sig) {
      teardownAds();
      return;
    }
    resetAdProvider();
    const me = useAuth.getState().me;
    if (!was && me && me.plan !== 'plus' && !me.ads_eligible) {
      void endpoints
        .me()
        .then((m) => useAuth.getState().setMe(m))
        .catch(() => undefined);
    }
  }, [sig]);
}
